import {FacilitySubType} from '../../../../../submodules/map/mapiq-map/markers/NodeMarkerData';
import {DayName as DayNameUtil} from '@lib/utils';

export type WorkspaceReservationCountDto = {[key: string]: number};

export type WorkspaceReservationCountByDateDto = {[key: string]: WorkspaceReservationCountDto};

export type AccessProfileDto = {
  id: string;
  name: string;
  daysPerWeek: number;
  daysAhead: number;
  registrationDays: DayName[];
};

export type LocationProfileDto = {
  id: string;
  name: string;
  allowedLocationIds: string[];
  disallowedLocationIds: string[];
  allowAll: boolean;
};

export type ParkingQuotaProfileDto = {
  id: string;
  name: string;
  daysPerWeek: number;
};

export type AppUserDto = {
  id: string;
  initials: string;
  subscriptionId: string;
  firstName: string;
  lastName: string;
  email: string;
  defaultBuildingId: string;
  isNew: boolean;
  effectiveAccessProfile: AccessProfileDto;
  accessProfileType: ProfileType;
  effectiveLocationProfile: LocationProfileDto;
  locationProfileType: ProfileType;
  imageHash: string;
  parkingQuotaProfile: ParkingQuotaProfileDto;
  parkingLocationProfile: LocationProfileDto;
};

export type PreferencesDto = {
  userDismissedParkingSuggestionPopUp: boolean;
  userDismissedParkingIntroduction: boolean;
  licensePlate: string | null;
  licensePlates: string[] | null;
  threeDMapsPreferred: boolean;
};

export type WorkspaceLocationDto = {
  floorId: string | null;
  areaId: string | null;
  isDeskRequired: boolean;
  deskAmenities: string[];
};

export type ParkingLocationDto = {
  parkingLotId: string | null;
  parkingZoneId: string | null;
};

export type UpdatePreferencesResponseDto = {
  shifts_FirstLoginTime: string | null;
  shifts_LastReadWelcomeMessageTime: string | null;
  collaboration_FirstConnectionTime: string | null;
  collaboration_IgnoreMessageTime: string | null;
  shifts_FirstRegistrationTime: string | null;
  shifts_AdminCheckInApiInactivityWarningDismissed: boolean;
  parking_UserDismissedParkingSuggestionPopUp: boolean;
  parking_UserDismissedParkingIntroduction: boolean;
  parking_LicensePlate: string | null;
  parking_LicensePlates: string[] | null;
  threeDMapsPreferred: boolean;
};

export type ShiftUserWithPreferencesDto = {
  // Unused properties are left out for simplicity
  preferences: UpdatePreferencesResponseDto;
};

export type SuggestedDeskDto = {
  id: string;
  booked: Boolean;
  score: number;
  explanation: string;
};

export type DeskSuggestionDto = {
  id: string;
  desks: SuggestedDeskDto[];
};

export type FloorMapFacilityDto = {
  id: string;
  displayName: string;
  facilityType: FacilityTypeDto;
};

export type FacilityTypeDto = {
  id: FacilitySubType;
  displayName: string;
  lightIconUrl: string;
  boldIconUrl: string;
};

export type DeskBookingStatusDto = {[key: string]: boolean};

export type DeskDto = {
  id: string;
  name: string;
  areaId: string;
  isEnabled: boolean;
};

export type DeskAmenity =
  | 'AdjustableDesk'
  | 'Cubicle'
  | 'SingleMonitor'
  | 'DualMonitor'
  | 'LargeMonitor'
  | 'DockingStation'
  | 'HDMIAdaptor'
  | 'USBCAdaptor'
  | 'ErgonomicChair'
  | 'Keyboard'
  | 'LaptopStand'
  | 'Mouse'
  | 'Webcam';

export type ActivityType =
  | 'Focus'
  | 'Collaboration'
  | 'Private'
  | 'Call'
  | 'ShortStay'
  | 'CreativeWork'
  | 'Informal'
  | 'IndividualWork';

export type AreaDto = {
  id: string;
  name: string;
  capacity: number;
  hasBookableDesks: boolean;
  activityTypes: Array<ActivityType>;
  deskAmenities: Array<DeskAmenity>;
  workspaceBookingPolicyType: 'AreaBooking' | 'DeskBooking' | 'NonBookable';
};

export type FloorDto = {
  id: string;
  name: string;
  number: number;
  capacity: number;
  hasMap: boolean;
  areas: AreaDto[];
};

export type BuildingDto = {
  id: string;
  name: string;
  ianaTimezone: string;
  capacity: number;
  floors: FloorDto[];
  openingDays: DayName[];
  allowWorkdayWithoutWorkspace: boolean;
};

export type DayName = DayNameUtil;

export type ProfileType = 'Default' | 'Automatic' | 'Manual';

export type RegistrationPolicy = 'SameDay' | 'CreateSameDayDeleteBeforeMidnight';

export type ParkingSettings = {
  checkDuplicateLicensePlates: boolean;
  licensePlateIsRequired: boolean;
};

export type SettingsDto = {
  officeInstructions: string;
  registrationPolicy: RegistrationPolicy;
  defaultAccessProfile: string;
  defaultLocationProfile: string;
  companyName: string;
  contactEmailAddress: string;
  companyWebsite: string;
  effectivePrivacyPolicyUrl: string;
  invitationExpireDays: number;
  maxNumberOfGroupWorkspaceInvitees: number;
  userRetentionPeriodDays: number;
  shiftsRetentionPeriodDays: number;
  parking: ParkingSettings;
};

export type ValidationErrorDto = {[key: string]: string[]};

export type ValidationProblemDetailsDto = {
  title: string;
  status: number;
  detail: string;
  errors: ValidationErrorDto;
};

export type PersonDTO = {
  id: string;
  name: string;
  initials: string;
  email: string;
  isActive: boolean;
  directoryObjectId: string;
  imageHash: string;
  isNew: boolean;
};

export type ConnectionUserDto = {
  id: string;
  name: string;
  initials: string;
  email: string;
  isActive: boolean;
  directoryObjectId: string;
  imageHash: string;
  isNew: boolean;
};

export type ConnectionDirection = 'Incoming' | 'Outgoing';

export type ConnectionStatus = 'Pending' | 'Accepted' | 'Ignored';

export type ConnectionDto = {
  id: string;
  connectedUser: ConnectionUserDto;
  direction: ConnectionDirection;
  status: ConnectionStatus;
  creationTime: string;
};

export type BookedOnBehalfOfUserDto = {
  userId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  createdOrLastModifiedDate: string;
};

export type InstallationDto = {
  deviceId: string;
  platform: string;
  token: string;
  tags: string[];
  version: number;
};

export type NotificationPreferenceCategory =
  | 'ShiftReminder'
  | 'Connections'
  | 'Updates'
  | 'ReservationReminder'
  | 'GroupBookingInvitations';

export type NotificationPreferenceType = 'Email' | 'UserEmail' | 'PushNotification' | 'InApp';

export type NotificationPreferenceDto = {
  category: NotificationPreferenceCategory;
  type: NotificationPreferenceType;
  enabled: boolean;
};

export type NotificationCategoryWithTypesDto = {
  category: NotificationPreferenceCategory;
  types: NotificationPreferenceType[];
};

export type UserNotificationPreferenceDto = {
  userId: string;
  preferences: NotificationPreferenceDto[];
};

export type NotificationTypeDto =
  | 'NotifyBookingOnBehalf'
  | 'NotifyUpdateOnBehalf'
  | 'NotifyDeleteOnBehalf'
  | 'NotifyDeleteOnLocationClose'
  | 'NotifyInAppReservationInvitationCreated'
  | 'NotifyGroupInvitationCanceled'
  | 'NotifyReservationReminder'
  | 'NotifyReservationReminder'
  | 'NotifyNoShowDetected'
  | 'NotifyNoShowRoomReleased';

export type NotificationDtoMessageValue = {
  BuildingName?: string;
  FloorName?: string;
  InviteeCountText?: string;
  OrganizerName?: string;
  ShiftDate?: string;
  InviteeCount?: number;
  AdminName?: string;
  MinutesUntilRoomIsReleased?: string;
  MeetingRoomName?: string;
  MeetingStartAndEnd?: string;
  NoShowId?: string;
  Link?: string;
  StartDateTimeUtc?: string;
  EndDateTimeUtc?: string;
};

export type NotificationCustomActionType = 'NoShow';

export type NotificationDto = {
  id: string;
  type: NotificationTypeDto;
  isRead: boolean;
  messageTemplate: string;
  messageTitle: string;
  message: string;
  messageValues: NotificationDtoMessageValue;
  creationDateTime: string;
  customAction?: NotificationCustomActionType;
  customActionDeadline?: string;
  isCustomActionPerformed?: boolean;
};

export type OpeningDayExceptionDto = {
  id: string;
  reason: string;
  startDate: string;
  endDate: string;
  closedNodeIds: string[];
};

export type GlobalMeetingsSettingsDto = {
  pageTimespanInDays: number;
};

export type MeetingsSettingsDto = {
  enabledCalendarIntegration: boolean;
  enabledOffice365: boolean;
  enabledGoogleWorkspace: boolean;
  onlineMeetingLinkSupported: boolean;
};

type WorkingHoursDayInfoDto = {
  day: DayName;
  times: {
    startTimeLocal: string;
    endTimeLocal: string;
  }[];
};

export type WorkingHoursDto = {
  ianaLocalTimezone: string;
  days: WorkingHoursDayInfoDto[];
};

export type MeetingsUserPreferencesDto = {
  enableCalendar: boolean;
  userClosedConsentTile: boolean;
  userClosedConsentErrorTile: boolean;
  workingHours: WorkingHoursDto | null;
  calendarDomain?: string | null;
};

export interface UserPreferencesDto extends MeetingsUserPreferencesDto, PreferencesDto {
  workspaceLocation: WorkspaceLocationDto | null;
  parkingLocation: ParkingLocationDto | null;
}

export type MeetingAttendeeType = 'Local' | 'Remote' | 'Unknown';

export type MeetingResponseStatus = 'Accepted' | 'Declined' | 'Tentative' | 'Pending' | 'Organizer';

export type AttendeeLocationDto = {
  locationType: MeetingAttendeeType;
  shiftBuildingId?: string;
  shiftBuildingName: string;
};

export type MeetingAttendeeDto = {
  directoryObjectId?: string;
  email: string;
  displayName: string;
  initials: string;
  responseStatus: MeetingResponseStatus;
  locationInfoPerDate: Record<string, AttendeeLocationDto | undefined>;
};

export type MeetingRoomDto = {
  id: string;
  displayName: string;
  email: string;
  capacity: number;
  activities: string[];
  equipment: string[];
  buildingId: string;
  buildingName: string;
  floorId: string;
  floorName: string;
  roomPictureHash?: string;
};

export type MeetingRoomAvailabilityAndSuggestionDto = MeetingRoomDto & {
  email?: string;
  state: RoomState;
  suggestionData?: RoomSuggestionDataDto;
  scheduleInformation: ScheduleInformationItemDto[];
  scheduleAvailability: AttendeeAvailabilityStatus;
  sensorAvailability: SensorAvailabilityStatus;
  /**
   * Number of people detected by sensors.
   */
  numberOfPeople?: number;
  sensorStateTimestamp?: string;
};

export type MeetingDto = {
  id?: string;
  title: string;
  startDateTime: string;
  endDateTime: string;
  isAllDay: boolean;
  isCanceled: boolean;
  meetingRooms: MeetingRoomDto[];
  attendees: MeetingAttendeeDto[];
  onlineMeetingUrl?: string;
  message?: string;
  messagePreview?: string;
  organizerTimeZone?: string;
};

export type MeetingConsentStatusDto = {
  isValidRefreshToken: boolean;
};

export enum CheckInEventType {
  AtTheOffice = 'AtTheOffice',
  NotAtTheOffice = 'NotAtTheOffice',
  NotCheckedInYet = 'NotCheckedInYet',
}

export enum CheckInSourceType {
  Manual = 'Manual',
  Automatic = 'Automatic',
}

export type CheckInStatusDto = {
  id?: string;
  userId?: string;
  directoryObjectId?: string;
  eventType: CheckInEventType;
  buildingId?: string;
  sourceType?: CheckInSourceType;
};

export type MeetingRequestDto = {
  startDateTime: string;
  endDateTime: string;
  title: string;
  message: string;
  attendees: string[];
  roomEmails: string[];
  isOnlineMeeting: boolean;
  isAllDay: boolean;
  organizerTimeZone?: string;
  calendarDomain: string | null | undefined;
};

export type MeetingSuggestionsRequestDto = {
  attendees: string[];
  durationInMinutes: number;
  startDateTime: string;
  endDateTime: string;
  buildingId?: string;
  calendarDomain: string | null | undefined;
};

export type AttendeeAvailabilityStatus = 'Available' | 'Tentative' | 'Unavailable' | 'Unknown';

export enum SensorAvailabilityStatus {
  Available = 'Available',
  Away = 'Away',
  Occupied = 'Occupied',
  Unknown = 'Unknown',
}

export enum RoomState {
  BookingAboutToStartAndFree = 'BookingAboutToStartAndFree',
  BookingAboutToStartAndInUse = 'BookingAboutToStartAndInUse',
  BookingAboutToStart = 'BookingAboutToStart',
  BookingAboutToStartAndUnknownSensor = 'BookingAboutToStartAndUnknownSensor',
  BookedAndFree = 'BookedAndFree',
  BookedAndInUse = 'BookedAndInUse',
  Booked = 'Booked',
  BookedAndUnknownSensor = 'BookedAndUnknownSensor',
  BookingAboutToEndAndFree = 'BookingAboutToEndAndFree',
  BookingAboutToEndAndInUse = 'BookingAboutToEndAndInUse',
  BookingAboutToEnd = 'BookingAboutToEnd',
  BookingAboutToEndAndUnknownSensor = 'BookingAboutToEndAndUnknownSensor',
  NotBookedAndFree = 'NotBookedAndFree',
  NotBookedAndInUse = 'NotBookedAndInUse',
  NotBooked = 'NotBooked',
  NotBookedAndUnknownSensor = 'NotBookedAndUnknownSensor',
  Free = 'Free',
  InUse = 'InUse',
  Untraceable = 'Untraceable',
  UnknownSensor = 'UnknownSensor',
  Unknown = 'Unknown',
}

export namespace RoomState {
  export function anyKindOfBooked(state: RoomState): boolean {
    return (
      state === RoomState.Booked ||
      state === RoomState.BookedAndFree ||
      state === RoomState.BookedAndInUse ||
      state === RoomState.BookedAndUnknownSensor
    );
  }

  export function anyKindOfBookingAboutToStart(state: RoomState): boolean {
    return (
      state === RoomState.BookingAboutToStart ||
      state === RoomState.BookingAboutToStartAndFree ||
      state === RoomState.BookingAboutToStartAndInUse ||
      state === RoomState.BookingAboutToStartAndUnknownSensor
    );
  }

  export function anyKindOfBookingAboutToEnd(state: RoomState): boolean {
    return (
      state === RoomState.BookingAboutToEnd ||
      state === RoomState.BookingAboutToEndAndFree ||
      state === RoomState.BookingAboutToEndAndInUse ||
      state === RoomState.BookingAboutToEndAndUnknownSensor
    );
  }
}

export type ScheduleInformationItemDto = {
  start: string;
  end: string;
  availability: AttendeeAvailabilityStatus;
};

export type RoomSuggestionDataDto = {
  suggestionId?: string;
  score: number;
};

export type AttendeeAvailabilityDto = {
  displayName: string;
  email: string;
  availability: AttendeeAvailabilityStatus;
  initials: string;
  directoryObjectId: string;
  shiftBuildingId: string;
  shiftBuildingName: string;
  locationType: MeetingAttendeeType;
  imageHash: string;
};

export type AvailabilityOfInviteesRequestDto = {
  emails: string[];
  startDateTimeUTC: string;
  endDateTimeUTC: string;
};

export type MeetingSuggestionDto = {
  timeSlotStart: string;
  timeSlotEnd: string;
  attendeeAvailabilities: AttendeeAvailabilityDto[];
  rooms: MeetingRoomDto[];
};

export type MeetingInviteeSearchResultDto = {
  /**
   * This is the id of the connection
   */
  id: string;
  name: string;
  initials: string;
  email: string;
  imageHash: string;
  directoryObjectId: string;
};

export type ParkingZoneDto = {
  id: string;
  name: string;
  capacity: number;
  parentId: string;
};

export type ParkingLotDto = {
  id: string;
  name: string;
  capacity: number;
  parkingZones: ParkingZoneDto[];
  openingDays: DayName[];
  buildingIds: string[];
};

export type ParkingReservationDto = {
  id: string;
  date: string;
  nodeId: string;
  parkingLotId: string;
  zoneId: string;
  parkingLotName: string;
  zoneName: string;
};

export type ParkingCountByDateDto = {[key: string]: ParkingCountDto};

export type ParkingCountDto = {[key: string]: number};

export type WorkspaceReservationStatus =
  | 'InvitationDeclined'
  | 'InvitationExpired'
  | 'InvitationOverwritten'
  | 'InvitationCancelled'
  | 'Confirmed'
  | 'InvitationPending'
  | 'None';

type WorkspaceLocation = {
  buildingId: string;
  buildingName: string;
  floorId?: string;
  floorName?: string;
  areaId?: string;
  areaName?: string;
  deskId?: string;
  deskName?: string;
};

type ReservationMemberDto = {
  userId: string;
  reservationId: string;
  user: {
    id: string;
    directoryObjectId?: string;
    imageHash?: string;
    email: string;
    initials: string;
    firstName?: string;
    lastName?: string;
  };
  nodeId: string;
  location: WorkspaceLocation;
  status: WorkspaceReservationStatus;
  remainingDaysToReply?: number;
  expiryDateTime?: string;
  isOwner: boolean;
};
export type GroupWorkspaceReservationDto = {
  id: string;
  startDate: string;
  endDate: string;
  owner: ReservationMemberDto;
  invitees: ReservationMemberDto[];
};

export type GroupWorkspaceReservationInviteeLiteDto = {
  reservationId: string;
  userId: string;
  nodeId: string;
  status: WorkspaceReservationStatus;
};

export type TimeZoneDto = {
  timeZoneId: string;
  timeZoneIdWithUtcOffset: string;
};

export type ConnectionSuggestionTypeDto = 'Random' | 'CommonFriends';

export type SuggestedConnectionDto = {
  connectedUser: ConnectionUserDto;
  score: number;
  numberOfCommonFriends: number;
  type: ConnectionSuggestionTypeDto;
};

export type ConnectionSuggestionDto = {
  id: string;
  userId: string;
  created: string;
  serveId: string;
  suggestions: SuggestedConnectionDto[];
};

export type ConnectionSuggestionFeedbackDto = {
  userId: string;
  suggestionId: string;
  userIdSuggestedConnection: string;
  created: string;
  serveId: string;
  approved: boolean;
};

export type CreateWorkspaceReservationCommand = {
  nodeId: string;
  startDate: string;
  endDate: string;
};

export type CreateWorkspaceReservationWithPreferredLocationCommand = {
  startDate: string;
  endDate: string;
};

export type WorkspaceReservationDto = {
  id: string;
  startDate: string;
  endDate: string;
  /**
   * @depreacted
   */
  nodeId: string;
  /**
   * @depreacted
   */
  userId: string;
  /**
   * @deprecated
   */
  buildingId: string;
  /**
   * @depreacted
   */
  buildingName: string;
  /**
   * @depreacted
   */
  floorId: string;
  floorName: string;
  /**
   * @depreacted
   */
  floorNumber?: number;
  /**
   * @depreacted
   */
  floorHasMap: boolean;
  /**
   * @depreacted
   */
  areaId: string;
  /**
   * @depreacted
   */
  areaName: string;
  /**
   * @depreacted
   */
  deskId: string;
  /**
   * @depreacted
   */
  deskName: string;
  createdBy: BookedOnBehalfOfUserDto;
  groupReservationId?: string;
  /**
   * @depreacted
   */
  status: WorkspaceReservationStatus;
  members: ReservationMemberDto[];
};

export type ConnectionWithReservationsDto = {
  user: ConnectionUserDto;
  workspaceReservations: WorkspaceReservationDto[];
  workdays: WorkdayDto[];
};

export type AllRoomsRequestDto = {
  internalAttendeeIds: string[];
  totalAttendees: number;
  buildingId: string;
  startDateTime: string;
  endDateTime: string;
  calendarDomain: string | null | undefined;
};

export type WorkdayStatusDto = 'OfficeDay' | 'NotWorking' | 'WorkingRemotely';

export type CreateWorkdayCommand = {
  nodeId?: string;
  status: WorkdayStatusDto;
  startDate: string;
  endDate: string;
};

export type WorkdayDto = {
  id: string;
  nodeId: string;
  nodeName: string;
  startDate: string;
  endDate: string;
  status: WorkdayStatusDto;
  userId: string;
};

/**
 * @depreacted
 */
export type ExtraTypeDto = 'Workday' | 'HybridMeetings' | 'UserProfile';
/**
 * @depreacted
 */
export enum ExtraName {
  BookCoworkingSpace = 'BookCoworkingSpace',
  BookLocker = 'BookLocker',
  BookLunch = 'BookLunch',
  RequestMedicalService = 'RequestMedicalService',
  RegisterVisitor = 'RegisterVisitor',
  ReportProblem = 'ReportProblem',
  RequestCatering = 'RequestCatering',
  RequestEquipment = 'RequestEquipment',
  RequestSeatingArrangement = 'RequestSeatingArrangement',
  ControlBlinds = 'ControlBlinds',
  ManageEmergencyContact = 'ManageEmergencyContact',
  ManageLicensePlate = 'ManageLicensePlate',
  RequestSupport = 'RequestSupport',
  InviteVisitors = 'InviteVisitors',
}
/**
 * @depreacted
 */
export type ExtraDto = {
  id: string;
  url: string;
  description: string;
  name: ExtraName;
  type: ExtraTypeDto;
};

export type ExtraServiceType =
  | 'BookCoworkingSpace'
  | 'BookLocker'
  | 'BookLunch'
  | 'RequestMedicalService'
  | 'RegisterVisitor'
  | 'ReportProblem'
  | 'RequestCatering'
  | 'RequestEquipment'
  | 'RequestSeatingArrangement'
  | 'ControlBlinds'
  | 'ManageEmergencyContact'
  | 'ManageLicensePlate'
  | 'RequestSupport'
  | 'InviteVisitors';
export type ExtraServiceEmailResource = {
  type: 'Email';
  to: string;
  subject: string;
  content?: string;
};
export type ExtraServiceUrlResource = {
  type: 'Url' | 'Form';
  url: string;
};
export type ExtrasResourceDto = ExtraServiceEmailResource | ExtraServiceUrlResource;

export type ExtraServiceDto = {
  id: string;
  title: string;
  description?: string;
  serviceType: ExtraServiceType;
  resource: ExtrasResourceDto;
  buildingIds: string[];
} & (
  | {
      category: 'General' | 'WorkdayBased' | 'EventBased';
    }
  | {
      category: 'LocationBased';
      locationTypes: ('MeetingRoom' | 'Area' | 'Facility')[];
    }
);

type Node<T extends 'Area' | 'Floor' | 'Building'> = {
  type: T;
  id: string;
  displayName: string;
};
export type WorkspaceOccupancyState = {
  node: Node<'Area'> & {
    parents: {
      Building: Node<'Building'>;
      Floor: Node<'Floor'>;
    };
  };
  stateTimestamp?: string; // TODO: optional?
  workplaceState: {
    nrPresent: number;
    nrTentative: number;
    nrUnknown: number;
    nrDesks: number;
  };
  desks: {
    id: string;
    deskIslandId: string;
    stateTimestamp?: string;
    state: SensorAvailabilityStatus;
  }[];
};

// These types differ from previous types, since they come directly
// from the building service. I.e. it's the real resources, not the
// view on those resources formed by the Shifts Service.
type FacilityResourceDto = {
  id: string;
  displayName: string | null;
  facilityType: {
    id: string;
    displayName: string;
    lightIconUrl: string;
    boldIconUrl: string;
  };
};

type MeetingRoomResourceDto = {
  id: string;
  displayName: string;
  email: string;
  capacity: 0;
  activities: string[];
  equipment: string[];
  roomPictureHash: string;
};

type DeskResourceDto = {
  id: string;
  displayName: string;
  isEnabled: boolean;
  index: number;
};

type DeskIslandResourceDto = {
  id: string;
  displayName: string;
  layout: 'singleRow' | 'doubleRow';
  desks: DeskResourceDto[];
  deskAmenities: string[];
};

type AreaResourceDto = {
  id: string;
  displayName: string;
  capacity: 0;
  workspaceBookingPolicyType: 'AreaBooking' | 'DeskBooking' | 'NonBookable';
  deskIslands: DeskIslandResourceDto[];
  spaceLayout: {
    id: 'closed' | 'open';
    displayName: string;
  };
  deskAmenities: string[];
  activityTypes: string[];
};

type FloorResourceDto = {
  id: string;
  displayName: string;
  capacity: number;
  level: number;
  areas: AreaResourceDto[];
  meetingRooms: MeetingRoomResourceDto[];
  facilities: FacilityResourceDto[];
};

export type BuildingResourceDto = {
  id: string;
  ianaTimeZoneId: string;
  displayName: string;
  capacity: number;
  floors: FloorResourceDto[];
  openingDays: ('monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday')[];
};

export type CustomerBrandingResourceDto = {
  displayName: string;
  subscriptionId: string;
  logoFile: string; //base64
  logo: string; //base64
  background: string;
  logoImageHash: string;
  logoFileType: string;
  logoFileName: string;
  backgroundFile: string;
  backgroundImageHash: string;
  backgroundFileType: string;
  backgroundFileName: string;
};

export type MySubscriptionResourseDto = {
  subscriptionId: string;
  logoHash: string;
  backgroundHash: string;
  displayName: string;
};

export enum NoShowReaction {
  NoResponse = 'NoResponse',
  RoomReleaseApproved = 'RoomReleaseApproved',
  RoomReleaseRejected = 'RoomReleaseRejected',
}

export type UserSettingsDto = {
  accessControl?: AccessControlSettingsDto;
};
export type AccessControlSettingsDto = {
  swiftConnect?: SwiftConnectSettingsDto;
};
export type SwiftConnectSettingsDto =
  | {
      clientId: string;
      authority: string;
      isEnabledForUser: true;
      baseUrl: string;
      origoUuid: string;
      applicationId: string;
      organizationName: string;
    }
  | {
      clientId: null;
      authority: null;
      isEnabledForUser: false;
      baseUrl: null;
      origoUuid: null;
      applicationId: null;
      organizationName: null;
    };

export type MeetingRoomAmenitiesDto = {
  activities: string[];
  equipment: string[];
};
export type BuildingMeetingRoomAmenitiesDto = Record<string, MeetingRoomAmenitiesDto | undefined>;

// Employee communication
export interface AnnouncementDto {
  /** @format guid */
  id: string;
  title: string;
  content: string;
  /** @format uri */
  link?: string;
  /** @format date-time */
  startDateTimeLocal: string;
  /** @format date-time */
  endDateTimeLocal: string;
  targetFilter: TargetFilter;
}

export type TargetFilter = AllTargetFilterType | WorkingLocationTargetFilterType | DefaultBuildingTargetFilterType;

export type TargetFilterType = TargetFilter['targetType'];

export type AllTargetFilterType = {
  /** Target all users */
  targetType: 'All';
};

export type WorkingLocationTargetFilterType = {
  /** Target users based on their working location */
  targetType: 'WorkingLocation';
  /** Building identifier */
  buildingId: string;
  /** Optional floor identifier */
  floorId?: string;
  /** Optional area identifier */
  areaId?: string;
};

export type DefaultBuildingTargetFilterType = {
  /** Target users based on their default building */
  targetType: 'DefaultBuilding';
  /** Default building identifier */
  buildingId: string;
};

// WIP
export enum IntentType {
  None = 'None',
  ControlBlindsOrTemperature = 'ControlBlindsOrTemperature',
}
export namespace IntentType {
  export function parse(value: string | undefined | null): IntentType | null {
    if (Object.values(IntentType).includes(value as IntentType)) {
      return value as IntentType;
    }

    return null;
  }
}

export const linkedLocationNodeTypes = ['MeetingRoom', 'Area', 'Facility'] as const;
export type LinkedLocationNodeType = (typeof linkedLocationNodeTypes)[number];
type BaseLinkedLocationDto = {
  buildingName: string;
  buildingId: string;

  floorName: string;
  floorLevel: number;
  floorId: string;

  nodeName: string;
  nodeType: LinkedLocationNodeType;
  nodeId: string;
};

export type AppLinkLinkedLocationDto = BaseLinkedLocationDto;
export type AppLinkDto = {
  id: string;
  intent: IntentType;
  location: AppLinkLinkedLocationDto;
};

export type ControlLinkedLocationDto = BaseLinkedLocationDto;
export type ControllableDto = {
  id: string;
  displayName: string;
  type: 'blinds' | 'temperature';
  locations: ControlLinkedLocationDto[];
  value?: number;
};
